// src/Routes.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter, Routes as RouterRoutes, Route } from 'react-router-dom';
import ScrollToTop from 'components/ScrollToTop';
import ErrorBoundary from 'components/ErrorBoundary';
import ProtectedRoute from './app/components/ProtectedRoute';
import StaffRoute from './app/components/StaffRoute';
import PrivacyRoute from './app/components/PrivacyRoute';
import { OnboardingGuard } from './shared/guards/OnboardingGuard';
import { ModuleRoute } from './shared/components/ModuleRoute';
import ModuleLoadingFallback from './components/ui/ModuleLoadingFallback';

// Lazy load pages for code splitting
const LandingPage = React.lazy(() => import(/* webpackChunkName: "landing" */ 'pages/LandingPage'));
const DocumentationPage = React.lazy(
  () => import(/* webpackChunkName: "public-docs" */ 'pages/public/DocumentationPage')
);
const ApiReferencePage = React.lazy(
  () => import(/* webpackChunkName: "public-api" */ 'pages/public/ApiReferencePage')
);
const ChangelogPage = React.lazy(
  () => import(/* webpackChunkName: "public-changelog" */ 'pages/public/ChangelogPage')
);
const PrivacyPolicyPage = React.lazy(
  () => import(/* webpackChunkName: "public-privacy" */ 'pages/public/PrivacyPolicyPage')
);
const TermsOfServicePage = React.lazy(
  () => import(/* webpackChunkName: "public-terms" */ 'pages/public/TermsOfServicePage')
);
const ContactUsPage = React.lazy(
  () => import(/* webpackChunkName: "public-contact" */ 'pages/public/ContactUsPage')
);
// ETH-04: public, unauthenticated whistleblower intake/status pages, wired to
// the AllowAny apps.ethics.views_anonymous endpoints.
const WhistleblowerReportPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-whistleblower-report" */ 'pages/public/WhistleblowerReportPage'
    )
);
const WhistleblowerStatusPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "public-whistleblower-status" */ 'pages/public/WhistleblowerStatusPage'
    )
);
const ComplianceDashboardOverview = React.lazy(
  () => import(/* webpackChunkName: "dashboard" */ 'pages/compliance-dashboard-overview')
);
const ControlsMatrixManagement = React.lazy(
  () => import(/* webpackChunkName: "controls" */ 'pages/controls-matrix-management')
);
const AuditTimelineScheduler = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-timeline-scheduler')
);
const PolicyLibraryManagement = React.lazy(
  () => import(/* webpackChunkName: "governance" */ 'pages/policy-library-management')
);
const MyActionItemsPage = React.lazy(
  () => import(/* webpackChunkName: "governance" */ 'pages/governance-management/my-action-items')
);
const MyPolicyAcknowledgments = React.lazy(
  () =>
    import(
      /* webpackChunkName: "governance" */ 'pages/policy-library-management/my-acknowledgments'
    )
);
const ControlDetailManagement = React.lazy(
  () => import(/* webpackChunkName: "controls" */ 'pages/control-detail-management')
);
const ComplianceReportingCenter = React.lazy(
  () => import(/* webpackChunkName: "reporting" */ 'pages/compliance-reporting-center')
);
const UserRoleAdministration = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/user-role-administration')
);
const OrganisationPage = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/organisation')
);
const ApprovalsInboxPage = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/approvals-inbox')
);
const EvidenceRequestsPage = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/evidence-requests')
);
const ApprovalItemPage = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/approval-item')
);
const ApprovalSettingsPage = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/approval-settings')
);
const DeadlineSettingsPage = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/deadline-settings')
);
const PlatformSecurityPage = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/platform-security')
);
const PlatformDeadlinesPage = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/platform-deadlines')
);
const AiSettingsPage = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/ai-settings')
);
const PlatformAiPage = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/platform-ai')
);
const PlatformRegulatorsPage = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/platform-regulators')
);
const RegulatoryCirculars = React.lazy(
  () => import(/* webpackChunkName: "compliance" */ 'pages/regulatory-circulars')
);
const RegulatoryCircularDetail = React.lazy(
  () => import(/* webpackChunkName: "compliance" */ 'pages/regulatory-circulars/detail')
);
const OwnershipReportPage = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/ownership-report')
);
const SystemConfigurationDashboard = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/system-configuration-dashboard')
);
const UserProfileManagement = React.lazy(
  () => import(/* webpackChunkName: "profile" */ 'pages/user-profile-management')
);
const ModuleManagementDashboard = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/settings/ModuleManagementDashboard')
);
const ModuleSettingsPage = React.lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/settings/ModuleSettingsPage')
);
const NotFound = React.lazy(() => import(/* webpackChunkName: "error" */ 'pages/NotFound'));

// Risk Management module
const RiskManagementDashboard = React.lazy(
  () => import(/* webpackChunkName: "risk" */ 'pages/risk-management')
);
const RiskRegister = React.lazy(
  () => import(/* webpackChunkName: "risk" */ 'pages/risk-management/register')
);
const RiskDetailPage = React.lazy(
  () => import(/* webpackChunkName: "risk" */ 'pages/risk-management/detail')
);
const CreateRisk = React.lazy(
  () => import(/* webpackChunkName: "risk" */ 'pages/risk-management/create')
);
const EditRisk = React.lazy(
  () => import(/* webpackChunkName: "risk" */ 'pages/risk-management/edit')
);
const RiskIncidentsPage = React.lazy(
  () => import(/* webpackChunkName: "risk" */ 'pages/risk-management/incidents')
);
const KCIManagementPage = React.lazy(
  () => import(/* webpackChunkName: "risk" */ 'pages/risk-management/kci')
);
const LossEventsPage = React.lazy(
  () => import(/* webpackChunkName: "risk" */ 'pages/risk-management/loss-events')
);

// Compliance Management module
const ComplianceManagementDashboard = React.lazy(
  () => import(/* webpackChunkName: "compliance" */ 'pages/compliance-management')
);
const ObligationsRegister = React.lazy(
  () => import(/* webpackChunkName: "compliance" */ 'pages/compliance-management/obligations')
);
const CreateObligation = React.lazy(
  () => import(/* webpackChunkName: "compliance" */ 'pages/compliance-management/create')
);
const ObligationDetail = React.lazy(
  () => import(/* webpackChunkName: "compliance" */ 'pages/compliance-management/obligation-detail')
);
const FilingCalendarPage = React.lazy(
  () => import(/* webpackChunkName: "compliance" */ 'pages/filing-calendar')
);
const CompliancePrivacyPage = React.lazy(
  () => import(/* webpackChunkName: "compliance" */ 'pages/compliance-management/privacy')
);
const ComplianceAttestationsPage = React.lazy(
  () => import(/* webpackChunkName: "compliance" */ 'pages/compliance-management/attestations')
);
const ComplianceExceptionsPage = React.lazy(
  () => import(/* webpackChunkName: "compliance" */ 'pages/compliance-management/exceptions')
);
const ComplianceControlTestsPage = React.lazy(
  () => import(/* webpackChunkName: "compliance" */ 'pages/compliance-management/control-tests')
);

// Audit Management module
const AuditManagementDashboard = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-management')
);
const AuditPlans = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-management/plans')
);
const CreateAuditPlan = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-management/create')
);
const PlanDetail = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-management/plan-detail')
);
const AuditEngagements = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-management/engagements')
);
const EngagementCreate = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-management/engagement-create')
);
const EngagementDetail = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-management/engagement-detail')
);
const AuditFindingsPage = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-management/findings')
);
const AuditWorkpapersPage = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-management/workpapers')
);
const AuditMyEngagementsPage = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-management/my-engagements')
);
const AuditTemplatesPage = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-management/templates')
);
const AuditUniversePage = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-management/universe')
);
const AuditTrailPage = React.lazy(
  () => import(/* webpackChunkName: "audit" */ 'pages/audit-trail')
);

// Control Library module
const ControlLibraryDashboard = React.lazy(
  () => import(/* webpackChunkName: "controls" */ 'pages/control-library')
);
const ControlLibraryCreate = React.lazy(
  () => import(/* webpackChunkName: "controls" */ 'pages/control-library/create')
);
const ControlsLibraryManagement = React.lazy(
  () => import(/* webpackChunkName: "controls" */ 'pages/controls-library-management')
);
const ControlsLibraryManagementEdit = React.lazy(
  () => import(/* webpackChunkName: "controls" */ 'pages/controls-library-management/edit')
);

// KRI Management module
const KRIManagementDashboard = React.lazy(
  () => import(/* webpackChunkName: "kri" */ 'pages/kri-management')
);
const KRIMasterList = React.lazy(
  () => import(/* webpackChunkName: "kri" */ 'pages/kri-management/master')
);
const CreateKRI = React.lazy(
  () => import(/* webpackChunkName: "kri" */ 'pages/kri-management/create')
);
const KRIDetail = React.lazy(
  () => import(/* webpackChunkName: "kri" */ 'pages/kri-management/detail')
);
const EditKRI = React.lazy(() => import(/* webpackChunkName: "kri" */ 'pages/kri-management/edit'));

// Additional domain modules
const GovernanceManagementDashboard = React.lazy(
  () => import(/* webpackChunkName: "governance" */ 'pages/governance-management')
);
const GovernanceObjectivesPage = React.lazy(
  () => import(/* webpackChunkName: "governance" */ 'pages/governance-management/objectives')
);
const CommitteeCreatePage = React.lazy(
  () => import(/* webpackChunkName: "governance" */ 'pages/governance-management/committee-create')
);
const MeetingCreatePage = React.lazy(
  () => import(/* webpackChunkName: "governance" */ 'pages/governance-management/meeting-create')
);
const EthicsManagementDashboard = React.lazy(
  () => import(/* webpackChunkName: "ethics" */ 'pages/ethics-management')
);
const CultureSurveysPage = React.lazy(
  () => import(/* webpackChunkName: "ethics" */ 'pages/ethics-management/culture-surveys')
);
const CultureSurveyDetailPage = React.lazy(
  () => import(/* webpackChunkName: "ethics" */ 'pages/ethics-management/culture-survey-detail')
);
const CodeOfConductPage = React.lazy(
  () => import(/* webpackChunkName: "ethics" */ 'pages/ethics-management/code-of-conduct')
);
const EthicsTrainingPage = React.lazy(
  () => import(/* webpackChunkName: "ethics" */ 'pages/ethics-management/training')
);
const GiftsPage = React.lazy(
  () => import(/* webpackChunkName: "ethics" */ 'pages/ethics-management/gifts')
);
const EthicsCaseCreatePage = React.lazy(
  () => import(/* webpackChunkName: "ethics" */ 'pages/ethics-management/case-create')
);
const EthicsCaseEditPage = React.lazy(
  () => import(/* webpackChunkName: "ethics" */ 'pages/ethics-management/case-edit')
);
const TechManagementDashboard = React.lazy(
  () => import(/* webpackChunkName: "tech" */ 'pages/tech-management')
);
const TechReleasesPage = React.lazy(
  () => import(/* webpackChunkName: "tech" */ 'pages/tech-management/releases')
);
const VendorsPage = React.lazy(
  () => import(/* webpackChunkName: "tech" */ 'pages/tech-management/vendors')
);
const VendorDetailPage = React.lazy(
  () => import(/* webpackChunkName: "tech" */ 'pages/tech-management/vendor-detail')
);
const ConfigBaselinesPage = React.lazy(
  () => import(/* webpackChunkName: "tech" */ 'pages/tech-management/config-baselines')
);
const ConfigDriftPage = React.lazy(
  () => import(/* webpackChunkName: "tech" */ 'pages/tech-management/config-drift')
);
const AccessReviewsPage = React.lazy(
  () => import(/* webpackChunkName: "tech" */ 'pages/tech-management/access-reviews')
);
const AssetCreatePage = React.lazy(
  () => import(/* webpackChunkName: "tech" */ 'pages/tech-management/asset-create')
);
const AssetEditPage = React.lazy(
  () => import(/* webpackChunkName: "tech" */ 'pages/tech-management/asset-edit')
);
const AssetDetailPage = React.lazy(
  () => import(/* webpackChunkName: "tech" */ 'pages/tech-management/asset-detail')
);
const ChangeRequestCreatePage = React.lazy(
  () => import(/* webpackChunkName: "tech" */ 'pages/tech-management/change-create')
);
const ChangeDetailPage = React.lazy(
  () => import(/* webpackChunkName: "tech" */ 'pages/tech-management/change-detail')
);
const ChangeEditPage = React.lazy(
  () => import(/* webpackChunkName: "tech" */ 'pages/tech-management/change-edit')
);
const IntegrationsDashboard = React.lazy(
  () => import(/* webpackChunkName: "integration" */ 'pages/integrations')
);
const WebhooksPage = React.lazy(
  () => import(/* webpackChunkName: "integration" */ 'pages/integrations/WebhooksPage')
);
const AIGovernancePage = React.lazy(
  () => import(/* webpackChunkName: "ai-governance" */ 'pages/ai-governance')
);
const AIAssessmentsPage = React.lazy(
  () => import(/* webpackChunkName: "ai-governance" */ 'pages/ai-governance/AIAssessmentsPage')
);
const AIIncidentsPage = React.lazy(
  () => import(/* webpackChunkName: "ai-governance" */ 'pages/ai-governance/AIIncidentsPage')
);
const MasterDataOverview = React.lazy(() => import('pages/master-data'));
const MasterDataRiskTaxonomy = React.lazy(() => import('pages/master-data/risk-taxonomy'));
const MasterDataControlLibrary = React.lazy(() => import('pages/master-data/control-library'));
const MasterDataObligations = React.lazy(() => import('pages/master-data/obligations'));
const MasterDataAuditUniverse = React.lazy(() => import('pages/master-data/audit-universe'));
const MasterDataEthicsFramework = React.lazy(() => import('pages/master-data/ethics-framework'));
const MasterDataITAssets = React.lazy(() => import('pages/master-data/it-assets'));
const MasterDataLossEvents = React.lazy(() => import('pages/master-data/loss-events'));
const MasterDataImport = React.lazy(() => import('pages/master-data/import'));
const MasterDataImportHistory = React.lazy(() => import('pages/master-data/import/history'));
const MasterDataTaxonomy = React.lazy(() => import('pages/master-data/taxonomy'));
const MasterDataTaxonomyAnomalies = React.lazy(
  () => import('pages/master-data/taxonomy/anomalies')
);
const MasterDataLevel4Usage = React.lazy(() => import('pages/master-data/taxonomy/level4-usage'));

// Auth pages (to be created)
const Login = React.lazy(() => import(/* webpackChunkName: "auth" */ 'pages/auth/Login'));
const Signup = React.lazy(() => import(/* webpackChunkName: "auth" */ 'pages/Signup'));
const AcceptInvitation = React.lazy(
  () => import(/* webpackChunkName: "auth" */ 'pages/auth/AcceptInvitation')
);
const Onboarding = React.lazy(
  () => import(/* webpackChunkName: "onboarding" */ 'pages/Onboarding')
);
const MagicLinkVerify = React.lazy(
  () => import(/* webpackChunkName: "auth" */ 'pages/auth/MagicLinkVerify')
);
const MfaVerify = React.lazy(() => import(/* webpackChunkName: "auth" */ 'pages/auth/MfaVerify'));
const Unauthorized = React.lazy(
  () => import(/* webpackChunkName: "error" */ 'pages/auth/Unauthorized')
);

// Notifications
const NotificationsPage = React.lazy(
  () => import(/* webpackChunkName: "notifications" */ 'pages/NotificationsPage')
);

// Wrapper component for module-specific lazy loading with context
const ModuleSuspense = ({ children, moduleName }) => (
  <React.Suspense fallback={<ModuleLoadingFallback moduleName={moduleName} />}>
    {children}
  </React.Suspense>
);

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-primary-light border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="theme-text-secondary text-lg">Loading...</p>
    </div>
  </div>
);
ModuleSuspense.propTypes = {
  children: PropTypes.node.isRequired,
  moduleName: PropTypes.string.isRequired,
};

const Routes = () => {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <ErrorBoundary>
        <ScrollToTop />
        <React.Suspense fallback={<LoadingFallback />}>
          <main id="main-content" tabIndex={-1}>
            <RouterRoutes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/documentation" element={<DocumentationPage />} />
              <Route path="/api-reference" element={<ApiReferencePage />} />
              <Route path="/changelog" element={<ChangelogPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/contact-us" element={<ContactUsPage />} />
              {/* ETH-04: order matters -- /ethics/report/status before the bare
                /ethics/report so the more specific path isn't shadowed. */}
              <Route path="/ethics/report/status" element={<WhistleblowerStatusPage />} />
              <Route path="/ethics/report" element={<WhistleblowerReportPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/accept-invitation" element={<AcceptInvitation />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/auth/magic-link/verify" element={<MagicLinkVerify />} />
              <Route path="/auth/mfa-verify" element={<MfaVerify />} />
              <Route path="/unauthorized" element={<Unauthorized />} />

              {/* Protected routes - require authentication */}
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/dashboard"
                  element={
                    <OnboardingGuard>
                      <ComplianceDashboardOverview />
                    </OnboardingGuard>
                  }
                />
                <Route
                  path="/compliance-dashboard-overview"
                  element={
                    <OnboardingGuard>
                      <ComplianceDashboardOverview />
                    </OnboardingGuard>
                  }
                />
                {/* Governance – specific routes before wildcard */}
                <Route
                  path="/governance/objectives"
                  element={
                    <ModuleSuspense moduleName="Governance Objectives">
                      <ModuleRoute moduleKey="governance">
                        <OnboardingGuard>
                          <GovernanceObjectivesPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/governance/committees/create"
                  element={
                    <ModuleSuspense moduleName="Create Committee">
                      <ModuleRoute moduleKey="governance">
                        <OnboardingGuard>
                          <CommitteeCreatePage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/governance/meetings/create"
                  element={
                    <ModuleSuspense moduleName="Schedule Meeting">
                      <ModuleRoute moduleKey="governance">
                        <OnboardingGuard>
                          <MeetingCreatePage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/governance-management/committees/create"
                  element={
                    <ModuleSuspense moduleName="Create Committee">
                      <ModuleRoute moduleKey="governance">
                        <OnboardingGuard>
                          <CommitteeCreatePage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/governance-management/meetings/create"
                  element={
                    <ModuleSuspense moduleName="Schedule Meeting">
                      <ModuleRoute moduleKey="governance">
                        <OnboardingGuard>
                          <MeetingCreatePage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/governance/*"
                  element={
                    <ModuleSuspense moduleName="Governance">
                      <ModuleRoute moduleKey="governance">
                        <OnboardingGuard>
                          <GovernanceManagementDashboard />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                {/* Ethics */}
                {/* ETH-13/14/15/18 (D8): self-service pages for every staff member; the
                    reviewer sections inside are gated on ethics:write. Declared before the
                    /ethics-management/* catch-all. */}
                <Route
                  path="/ethics-management/culture-surveys/:id"
                  element={
                    <ModuleSuspense moduleName="Culture Survey">
                      <ModuleRoute moduleKey="ethics">
                        <OnboardingGuard>
                          <CultureSurveyDetailPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/ethics-management/code-of-conduct"
                  element={
                    <ModuleSuspense moduleName="Code of Conduct">
                      <ModuleRoute moduleKey="ethics">
                        <OnboardingGuard>
                          <CodeOfConductPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/ethics-management/training"
                  element={
                    <ModuleSuspense moduleName="Ethics Training">
                      <ModuleRoute moduleKey="ethics">
                        <OnboardingGuard>
                          <EthicsTrainingPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/ethics-management/gifts"
                  element={
                    <ModuleSuspense moduleName="Gifts & Hospitality">
                      <ModuleRoute moduleKey="ethics">
                        <OnboardingGuard>
                          <GiftsPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/ethics-management/culture-surveys"
                  element={
                    <ModuleSuspense moduleName="Culture Surveys">
                      <ModuleRoute moduleKey="ethics">
                        <OnboardingGuard>
                          <CultureSurveysPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/ethics-management/cases/create"
                  element={
                    <ModuleSuspense moduleName="New Ethics Case">
                      <ModuleRoute moduleKey="ethics">
                        <OnboardingGuard>
                          <EthicsCaseCreatePage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/ethics-management/cases/:id/edit"
                  element={
                    <ModuleSuspense moduleName="Edit Ethics Case">
                      <ModuleRoute moduleKey="ethics">
                        <OnboardingGuard>
                          <EthicsCaseEditPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/ethics/*"
                  element={
                    <ModuleSuspense moduleName="Ethics">
                      <ModuleRoute moduleKey="ethics">
                        <OnboardingGuard>
                          <EthicsManagementDashboard />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/ethics-management/*"
                  element={
                    <ModuleSuspense moduleName="Ethics">
                      <ModuleRoute moduleKey="ethics">
                        <OnboardingGuard>
                          <EthicsManagementDashboard />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                {/* Tech Management – specific routes before wildcard */}
                <Route
                  path="/tech-management/releases"
                  element={
                    <ModuleSuspense moduleName="Releases">
                      <ModuleRoute moduleKey="tech_management">
                        <OnboardingGuard>
                          <TechReleasesPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/tech-management/vendors"
                  element={
                    <ModuleSuspense moduleName="Vendors">
                      <ModuleRoute moduleKey="tech_management">
                        <OnboardingGuard>
                          <VendorsPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/tech-management/vendors/:vendorId"
                  element={
                    <ModuleSuspense moduleName="Vendor">
                      <ModuleRoute moduleKey="tech_management">
                        <OnboardingGuard>
                          <VendorDetailPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/tech-management/config-baselines"
                  element={
                    <ModuleSuspense moduleName="Configuration Baselines">
                      <ModuleRoute moduleKey="tech_management">
                        <OnboardingGuard>
                          <ConfigBaselinesPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/tech-management/config-drift"
                  element={
                    <ModuleSuspense moduleName="Configuration Drift">
                      <ModuleRoute moduleKey="tech_management">
                        <OnboardingGuard>
                          <ConfigDriftPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/tech-management/access-reviews"
                  element={
                    <ModuleSuspense moduleName="Access Reviews">
                      <ModuleRoute moduleKey="tech_management">
                        <OnboardingGuard>
                          <AccessReviewsPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/tech-management/assets/create"
                  element={
                    <ModuleSuspense moduleName="Register Asset">
                      <ModuleRoute moduleKey="tech_management">
                        <OnboardingGuard>
                          <AssetCreatePage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/tech-management/assets/:id/edit"
                  element={
                    <ModuleSuspense moduleName="Edit Asset">
                      <ModuleRoute moduleKey="tech_management">
                        <OnboardingGuard>
                          <AssetEditPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                {/* TEC-08: previously missing entirely -- index.jsx's per-row
                  View button navigated here and fell through to the
                  "/tech-management/*" catch-all dashboard route below. */}
                <Route
                  path="/tech-management/assets/:id"
                  element={
                    <ModuleSuspense moduleName="Asset Detail">
                      <ModuleRoute moduleKey="tech_management">
                        <OnboardingGuard>
                          <AssetDetailPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/tech-management/changes/create"
                  element={
                    <ModuleSuspense moduleName="New Change Request">
                      <ModuleRoute moduleKey="tech_management">
                        <OnboardingGuard>
                          <ChangeRequestCreatePage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                {/* TEC-10: these were previously missing entirely -- index.jsx's
                  per-row View/Edit buttons navigated here and fell through to
                  the "/tech-management/*" catch-all dashboard route below. */}
                <Route
                  path="/tech-management/changes/:id/edit"
                  element={
                    <ModuleSuspense moduleName="Edit Change Request">
                      <ModuleRoute moduleKey="tech_management">
                        <OnboardingGuard>
                          <ChangeEditPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/tech-management/changes/:id"
                  element={
                    <ModuleSuspense moduleName="Change Request">
                      <ModuleRoute moduleKey="tech_management">
                        <OnboardingGuard>
                          <ChangeDetailPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/tech-management/*"
                  element={
                    <ModuleSuspense moduleName="IT Governance">
                      <ModuleRoute moduleKey="tech_management">
                        <OnboardingGuard>
                          <TechManagementDashboard />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/integrations/webhooks"
                  element={
                    <ModuleSuspense moduleName="Webhook Management">
                      <ModuleRoute moduleKey="integration">
                        <OnboardingGuard>
                          <WebhooksPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/integrations"
                  element={
                    <ModuleSuspense moduleName="Integrations">
                      <ModuleRoute moduleKey="integration">
                        <OnboardingGuard>
                          <IntegrationsDashboard />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                {/* GOV-09 (D8): any signed-in user acknowledges published policies, so this
                    page sits outside the compliance:read group below. */}
                <Route
                  path="/policy-library-management/my-acknowledgments"
                  element={
                    <ModuleSuspense moduleName="My Policy Acknowledgements">
                      <ModuleRoute moduleKey="governance">
                        <OnboardingGuard>
                          <MyPolicyAcknowledgments />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                {/* GOV-05 / D20: assignees complete their own meeting action items. */}
                <Route
                  path="/governance/my-action-items"
                  element={
                    <ModuleSuspense moduleName="My Action Items">
                      <ModuleRoute moduleKey="governance">
                        <OnboardingGuard>
                          <MyActionItemsPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                {/* CMP-22 (D8): attestations are completed by whoever they are assigned to,
                    so this page sits outside the compliance:read group. */}
                <Route
                  path="/compliance-management/attestations"
                  element={
                    <ModuleSuspense moduleName="Attestations">
                      <ModuleRoute moduleKey="compliance">
                        <OnboardingGuard>
                          <ComplianceAttestationsPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
              </Route>

              {/* Privacy page: compliance:read OR a named DPO (D2-O1: DPOs need no compliance
                  role). The page hides the registers a user cannot read; the APIs check again. */}
              <Route element={<ProtectedRoute />}>
                <Route element={<PrivacyRoute />}>
                  <Route
                    path="/compliance-management/privacy"
                    element={
                      <ModuleSuspense moduleName="Privacy & GDPR">
                        <ModuleRoute moduleKey="compliance">
                          <OnboardingGuard>
                            <CompliancePrivacyPage />
                          </OnboardingGuard>
                        </ModuleRoute>
                      </ModuleSuspense>
                    }
                  />
                </Route>
              </Route>

              {/* Compliance module - requires compliance:read permission AND module enabled */}
              <Route element={<ProtectedRoute requiredPermission="compliance:read" />}>
                <Route
                  path="/compliance-management"
                  element={
                    <ModuleSuspense moduleName="Compliance Management">
                      <ModuleRoute moduleKey="compliance">
                        <OnboardingGuard>
                          <ComplianceManagementDashboard />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/regulatory-circulars"
                  element={
                    <ModuleSuspense moduleName="Regulatory Circulars">
                      <ModuleRoute moduleKey="compliance">
                        <OnboardingGuard>
                          <RegulatoryCirculars />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/regulatory-circulars/:id"
                  element={
                    <ModuleSuspense moduleName="Regulatory Circular">
                      <ModuleRoute moduleKey="compliance">
                        <OnboardingGuard>
                          <RegulatoryCircularDetail />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/compliance-management/obligations"
                  element={
                    <ModuleSuspense moduleName="Obligations">
                      <ModuleRoute moduleKey="compliance">
                        <OnboardingGuard>
                          <ObligationsRegister />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/filing-calendar"
                  element={
                    <ModuleSuspense moduleName="Filing Calendar">
                      <ModuleRoute moduleKey="compliance">
                        <OnboardingGuard>
                          <FilingCalendarPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/compliance-management/obligations/create"
                  element={
                    <ModuleSuspense moduleName="Create Obligation">
                      <ModuleRoute moduleKey="compliance">
                        <OnboardingGuard>
                          <CreateObligation />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/compliance-management/obligations/:id"
                  element={
                    <ModuleSuspense moduleName="Obligation Detail">
                      <ModuleRoute moduleKey="compliance">
                        <OnboardingGuard>
                          <ObligationDetail />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/compliance-management/exceptions"
                  element={
                    <ModuleSuspense moduleName="Compliance Exceptions">
                      <ModuleRoute moduleKey="compliance">
                        <OnboardingGuard>
                          <ComplianceExceptionsPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/compliance-management/control-tests"
                  element={
                    <ModuleSuspense moduleName="Control Tests">
                      <ModuleRoute moduleKey="compliance">
                        <OnboardingGuard>
                          <ComplianceControlTestsPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/controls-matrix-management"
                  element={
                    <ModuleSuspense moduleName="Controls Matrix">
                      <ModuleRoute moduleKey="control">
                        <OnboardingGuard>
                          <ControlsMatrixManagement />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/policy-library-management"
                  element={
                    <ModuleSuspense moduleName="Policy Library">
                      <ModuleRoute moduleKey="governance">
                        <OnboardingGuard>
                          <PolicyLibraryManagement />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                {/* CTL-03: real detail view keyed by control id; bare path (no
                  prior caller ever linked to it) shows a select-a-control
                  prompt instead of the old hardcoded-data page. */}
                <Route
                  path="/control-detail-management/:id?"
                  element={
                    <ModuleSuspense moduleName="Control Details">
                      <ModuleRoute moduleKey="control">
                        <OnboardingGuard>
                          <ControlDetailManagement />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/compliance-reporting-center"
                  element={
                    <ModuleSuspense moduleName="Reporting Center">
                      <ModuleRoute moduleKey="compliance">
                        <OnboardingGuard>
                          <ComplianceReportingCenter />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />

                {/* Control Library module */}
                <Route
                  path="/control-library"
                  element={
                    <ModuleSuspense moduleName="Control Library">
                      <ModuleRoute moduleKey="control">
                        <OnboardingGuard>
                          <ControlLibraryDashboard />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                {/* CTL-02: create/edit pages for the control library */}
                <Route
                  path="/control-library/create"
                  element={
                    <ModuleSuspense moduleName="Create Control">
                      <ModuleRoute moduleKey="control">
                        <OnboardingGuard>
                          <ControlLibraryCreate />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/controls-library-management"
                  element={
                    <ModuleSuspense moduleName="Controls Library Management">
                      <ModuleRoute moduleKey="control">
                        <OnboardingGuard>
                          <ControlsLibraryManagement />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/controls-library-management/:id/edit"
                  element={
                    <ModuleSuspense moduleName="Edit Control">
                      <ModuleRoute moduleKey="control">
                        <OnboardingGuard>
                          <ControlsLibraryManagementEdit />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
              </Route>

              {/* Risk Management module - requires risk:read permission AND module enabled */}
              <Route element={<ProtectedRoute requiredPermission="risk:read" />}>
                <Route
                  path="/risk-management"
                  element={
                    <ModuleSuspense moduleName="Risk Management">
                      <ModuleRoute moduleKey="risk">
                        <OnboardingGuard>
                          <RiskManagementDashboard />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/risk-management/register"
                  element={
                    <ModuleSuspense moduleName="Risk Register">
                      <ModuleRoute moduleKey="risk">
                        <OnboardingGuard>
                          <RiskRegister />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/risk-management/detail/:id"
                  element={
                    <ModuleSuspense moduleName="Risk Details">
                      <ModuleRoute moduleKey="risk">
                        <OnboardingGuard>
                          <RiskDetailPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/risk-management/create"
                  element={
                    <ModuleSuspense moduleName="Create Risk">
                      <ModuleRoute moduleKey="risk">
                        <OnboardingGuard>
                          <CreateRisk />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/risk-management/:id/edit"
                  element={
                    <ModuleSuspense moduleName="Edit Risk">
                      <ModuleRoute moduleKey="risk">
                        <OnboardingGuard>
                          <EditRisk />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/risk-management/incidents"
                  element={
                    <ModuleSuspense moduleName="Risk Incidents">
                      <ModuleRoute moduleKey="risk">
                        <OnboardingGuard>
                          <RiskIncidentsPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/risk-management/kci"
                  element={
                    <ModuleSuspense moduleName="Key Control Indicators">
                      <ModuleRoute moduleKey="risk">
                        <OnboardingGuard>
                          <KCIManagementPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/risk-management/loss-events"
                  element={
                    <ModuleSuspense moduleName="Loss Events">
                      <ModuleRoute moduleKey="risk">
                        <OnboardingGuard>
                          <LossEventsPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />

                {/* KRI Management (depends on risk module) */}
                <Route
                  path="/kri-management"
                  element={
                    <ModuleSuspense moduleName="KRI Management">
                      <ModuleRoute moduleKey="kri">
                        <OnboardingGuard>
                          <KRIManagementDashboard />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/kri-management/master"
                  element={
                    <ModuleSuspense moduleName="KRI Master List">
                      <ModuleRoute moduleKey="kri">
                        <OnboardingGuard>
                          <KRIMasterList />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/kri-management/create"
                  element={
                    <ModuleSuspense moduleName="Create KRI">
                      <ModuleRoute moduleKey="kri">
                        <OnboardingGuard>
                          <CreateKRI />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/kri-management/:id"
                  element={
                    <ModuleSuspense moduleName="KRI Details">
                      <ModuleRoute moduleKey="kri">
                        <OnboardingGuard>
                          <KRIDetail />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/kri-management/:id/edit"
                  element={
                    <ModuleSuspense moduleName="Edit KRI">
                      <ModuleRoute moduleKey="kri">
                        <OnboardingGuard>
                          <EditKRI />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
              </Route>

              {/* Audit module - requires audit:read permission AND module enabled */}
              <Route element={<ProtectedRoute requiredPermission="audit:read" />}>
                <Route
                  path="/audit-management"
                  element={
                    <ModuleSuspense moduleName="Audit Management">
                      <ModuleRoute moduleKey="audit">
                        <OnboardingGuard>
                          <AuditManagementDashboard />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/audit-management/plans"
                  element={
                    <ModuleSuspense moduleName="Audit Plans">
                      <ModuleRoute moduleKey="audit">
                        <OnboardingGuard>
                          <AuditPlans />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/audit-management/plans/create"
                  element={
                    <ModuleSuspense moduleName="Create Audit Plan">
                      <ModuleRoute moduleKey="audit">
                        <OnboardingGuard>
                          <CreateAuditPlan />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/audit-management/plans/:id"
                  element={
                    <ModuleSuspense moduleName="Audit Plan Detail">
                      <ModuleRoute moduleKey="audit">
                        <OnboardingGuard>
                          <PlanDetail />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/audit-management/engagements"
                  element={
                    <ModuleSuspense moduleName="Audit Engagements">
                      <ModuleRoute moduleKey="audit">
                        <OnboardingGuard>
                          <AuditEngagements />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/audit-management/engagements/create"
                  element={
                    <ModuleSuspense moduleName="New Engagement">
                      <ModuleRoute moduleKey="audit">
                        <OnboardingGuard>
                          <EngagementCreate />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/audit-management/engagements/:id"
                  element={
                    <ModuleSuspense moduleName="Engagement Detail">
                      <ModuleRoute moduleKey="audit">
                        <OnboardingGuard>
                          <EngagementDetail />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/audit-management/findings"
                  element={
                    <ModuleSuspense moduleName="Audit Findings">
                      <ModuleRoute moduleKey="audit">
                        <OnboardingGuard>
                          <AuditFindingsPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/audit-management/workpapers"
                  element={
                    <ModuleSuspense moduleName="Audit Workpapers">
                      <ModuleRoute moduleKey="audit">
                        <OnboardingGuard>
                          <AuditWorkpapersPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/audit-management/my-engagements"
                  element={
                    <ModuleSuspense moduleName="My Engagements">
                      <ModuleRoute moduleKey="audit">
                        <OnboardingGuard>
                          <AuditMyEngagementsPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/audit-management/templates"
                  element={
                    <ModuleSuspense moduleName="Audit Templates">
                      <ModuleRoute moduleKey="audit">
                        <OnboardingGuard>
                          <AuditTemplatesPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/audit-management/universe"
                  element={
                    <ModuleSuspense moduleName="Audit Universe">
                      <ModuleRoute moduleKey="audit">
                        <OnboardingGuard>
                          <AuditUniversePage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/audit-timeline-scheduler"
                  element={
                    <ModuleSuspense moduleName="Audit Timeline">
                      <ModuleRoute moduleKey="audit">
                        <OnboardingGuard>
                          <AuditTimelineScheduler />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
              </Route>

              <Route element={<ProtectedRoute requiredPermission="audit_trail:read" />}>
                <Route
                  path="/audit-trail"
                  element={
                    <OnboardingGuard>
                      <AuditTrailPage />
                    </OnboardingGuard>
                  }
                />
              </Route>

              <Route element={<ProtectedRoute requiredPermission="ai_governance:read" />}>
                <Route
                  path="/ai-governance"
                  element={
                    <ModuleSuspense moduleName="AI Governance">
                      <ModuleRoute moduleKey="ai_governance">
                        <OnboardingGuard>
                          <AIGovernancePage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/ai-governance/assessments"
                  element={
                    <ModuleSuspense moduleName="AI Assessments">
                      <ModuleRoute moduleKey="ai_governance">
                        <OnboardingGuard>
                          <AIAssessmentsPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/ai-governance/incidents"
                  element={
                    <ModuleSuspense moduleName="AI Incidents">
                      <ModuleRoute moduleKey="ai_governance">
                        <OnboardingGuard>
                          <AIIncidentsPage />
                        </OnboardingGuard>
                      </ModuleRoute>
                    </ModuleSuspense>
                  }
                />
              </Route>

              <Route element={<ProtectedRoute requiredPermission="master_data:read" />}>
                <Route
                  path="/master-data"
                  element={
                    <ModuleSuspense moduleName="Master Data">
                      <MasterDataOverview />
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/master-data/taxonomy"
                  element={
                    <ModuleSuspense moduleName="Master Data">
                      <MasterDataTaxonomy />
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/master-data/taxonomy/anomalies"
                  element={
                    <ModuleSuspense moduleName="Master Data">
                      <MasterDataTaxonomyAnomalies />
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/master-data/taxonomy/level4-usage"
                  element={
                    <ModuleSuspense moduleName="Master Data">
                      <MasterDataLevel4Usage />
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/master-data/risk-taxonomy"
                  element={
                    <ModuleSuspense moduleName="Master Data">
                      <MasterDataRiskTaxonomy />
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/master-data/control-library"
                  element={
                    <ModuleSuspense moduleName="Master Data">
                      <MasterDataControlLibrary />
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/master-data/obligations"
                  element={
                    <ModuleSuspense moduleName="Master Data">
                      <MasterDataObligations />
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/master-data/audit-universe"
                  element={
                    <ModuleSuspense moduleName="Master Data">
                      <MasterDataAuditUniverse />
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/master-data/ethics-framework"
                  element={
                    <ModuleSuspense moduleName="Master Data">
                      <MasterDataEthicsFramework />
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/master-data/it-assets"
                  element={
                    <ModuleSuspense moduleName="Master Data">
                      <MasterDataITAssets />
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/master-data/loss-events"
                  element={
                    <ModuleSuspense moduleName="Master Data">
                      <MasterDataLossEvents />
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/master-data/import/history"
                  element={
                    <ModuleSuspense moduleName="Import History">
                      <MasterDataImportHistory />
                    </ModuleSuspense>
                  }
                />
                <Route
                  path="/master-data/import"
                  element={
                    <ModuleSuspense moduleName="Master Data">
                      <MasterDataImport />
                    </ModuleSuspense>
                  }
                />
              </Route>

              {/* My approvals - authenticated only; every user may hold approvals */}
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/approvals"
                  element={
                    <OnboardingGuard>
                      <ApprovalsInboxPage />
                    </OnboardingGuard>
                  }
                />
                {/* One item's approval: approvers may lack the module's read permission */}
                <Route
                  path="/approvals/item"
                  element={
                    <OnboardingGuard>
                      <ApprovalItemPage />
                    </OnboardingGuard>
                  }
                />
              </Route>

              {/* Evidence requests - authenticated only; an assignee may hold no role on the item's module */}
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/evidence-requests"
                  element={
                    <OnboardingGuard>
                      <EvidenceRequestsPage />
                    </OnboardingGuard>
                  }
                />
              </Route>

              {/* Department ownership report - authenticated only; the server scopes rows (403 if none) */}
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/ownership-report"
                  element={
                    <OnboardingGuard>
                      <OwnershipReportPage />
                    </OnboardingGuard>
                  }
                />
              </Route>

              {/* Organisation - org:read (System Configuration is admin-only, R11) */}
              <Route element={<ProtectedRoute requiredPermission="org:read" />}>
                <Route
                  path="/organisation"
                  element={
                    <OnboardingGuard>
                      <OrganisationPage />
                    </OnboardingGuard>
                  }
                />
              </Route>

              {/* Admin routes - requires admin permissions */}
              <Route element={<ProtectedRoute requiredPermission="admin:write" />}>
                <Route
                  path="/user-role-administration"
                  element={
                    <OnboardingGuard>
                      <UserRoleAdministration />
                    </OnboardingGuard>
                  }
                />
                <Route
                  path="/approval-settings"
                  element={
                    <OnboardingGuard>
                      <ApprovalSettingsPage />
                    </OnboardingGuard>
                  }
                />
                <Route
                  path="/deadline-settings"
                  element={
                    <OnboardingGuard>
                      <DeadlineSettingsPage />
                    </OnboardingGuard>
                  }
                />
                <Route
                  path="/ai-settings"
                  element={
                    <OnboardingGuard>
                      <AiSettingsPage />
                    </OnboardingGuard>
                  }
                />
                <Route
                  path="/system-configuration-dashboard"
                  element={
                    <OnboardingGuard>
                      <SystemConfigurationDashboard />
                    </OnboardingGuard>
                  }
                />
                <Route
                  path="/settings/modules"
                  element={
                    <OnboardingGuard>
                      <ModuleManagementDashboard />
                    </OnboardingGuard>
                  }
                />
                <Route
                  path="/settings/modules/:moduleKey"
                  element={
                    <OnboardingGuard>
                      <ModuleSettingsPage />
                    </OnboardingGuard>
                  }
                />
              </Route>

              {/* Platform AI and regulator register - platform staff only (is_staff; the APIs are IsAdminUser) */}
              <Route element={<ProtectedRoute />}>
                <Route element={<StaffRoute />}>
                  <Route path="/platform-ai" element={<PlatformAiPage />} />
                  <Route path="/platform-deadlines" element={<PlatformDeadlinesPage />} />
                  <Route path="/platform-security" element={<PlatformSecurityPage />} />
                  <Route path="/platform-regulators" element={<PlatformRegulatorsPage />} />
                </Route>
              </Route>

              {/* User profile - all authenticated users */}
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/user-profile-management"
                  element={
                    <OnboardingGuard>
                      <UserProfileManagement />
                    </OnboardingGuard>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <OnboardingGuard>
                      <NotificationsPage />
                    </OnboardingGuard>
                  }
                />
              </Route>

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </RouterRoutes>
          </main>
        </React.Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
